import { useNavigate, useSearch } from '@tanstack/react-router';
import { Ban, ChevronRight, Eye, RotateCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { cn } from '~/utils/cn';
import { formatDateTime } from '~/utils/date';

import { useBatchHeader } from '../../hooks/use-batch-detail';
import { useBatchCarryover, useBatchRuns } from '../../hooks/use-batch-retry';
import { useJourneyRuns, usePostSteps } from '../../hooks/use-post-step-retry';
import type { BatchRun, BatchRuns } from '../../types/batch-retry.api.types';
import type {
  JourneyStep,
  PostStepName,
  PostStepScope,
} from '../../types/post-step-retry.api.types';
import { isRetryStuck } from '../../utils/batch-retry-state';

import { BatchCarryoverSection, type CarryoverView } from './batch-carryover-section';
import { RunOutcomeBar, RunOutcomeLegend } from './batch-retry-shared';
import { formatCount, humanize, leftOverFilter, runTotal, SCOPE_LABEL } from './batch-retry-utils';
import { PostStepJourney } from './post-step-journey';
import {
  attentionOf,
  healthOf,
  isStepEnabled,
  JOURNEY_STEPS,
  type StepAttention,
  type StepKey,
} from './post-step-journey-utils';
import { PostStepStepper, type StepperItem } from './post-step-stepper';
import { RunProductsPanel, type GridFocus, type RunFilter } from './run-products-panel';

export interface BatchRunsTabProps {
  jobId: string;
  /** Opens the retry dialog for "Retry what retry #n left" (RETRY_RUN_FAILURES). */
  onRetryRun: (fromRunId: string) => void;
  /** Opens the retry dialog for everything still not enriched (RETRY_REMAINING). */
  onRetryRemaining: () => void;
  /** Opens the exclude dialog (stuck panel). */
  onExclude: () => void;
  /** Opens the retry dialog for a search-descriptor / phrase-description step. */
  onRetryPostStep?: (step: PostStepName, scope?: PostStepScope) => void;
  /**
   * Set by a "not enriched yet" link elsewhere on the page: scroll the selected run's products
   * into view once they render, then `onProductsFocused` clears it.
   */
  focusProducts?: boolean;
  onProductsFocused?: () => void;
  /**
   * Set by the Enriched card's "from / moved to … batches" link: scroll the carry-over section
   * into view once it renders, then `onCarryoverFocused` clears it.
   */
  focusCarryover?: boolean;
  onCarryoverFocused?: () => void;
}

const STATUS_TONE: Record<string, string> = {
  COMPLETED: 'bg-emerald-50 text-emerald-700',
  RUNNING: 'bg-info-50 text-info',
  PAUSED: 'bg-warning-50 text-warning',
  FAILED: 'bg-error-50 text-error',
  CANCELLED: 'bg-muted text-muted-foreground',
  TERMINATED: 'bg-muted text-muted-foreground',
  TIMED_OUT: 'bg-error-50 text-error',
  // Root-run statuses.
  IN_PROGRESS: 'bg-info-50 text-info',
  PENDING: 'bg-muted text-muted-foreground',
  AWAITING_APPROVAL: 'bg-warning-50 text-warning',
  DELIVERING: 'bg-info-50 text-info',
  REJECTED: 'bg-error-50 text-error',
};

const NODE_TONE: Record<string, string> = {
  COMPLETED: 'bg-emerald-500',
  RUNNING: 'bg-info',
  PAUSED: 'bg-warning',
  FAILED: 'bg-error',
  IN_PROGRESS: 'bg-info',
  PENDING: 'bg-neutral-400',
  AWAITING_APPROVAL: 'bg-warning',
  DELIVERING: 'bg-info',
  REJECTED: 'bg-error',
};

function runName(run: BatchRun): string {
  return run.kind === 'ROOT' ? 'Original run' : `Retry #${String(run.attempt)}`;
}

function leftOver(counts: BatchRuns['counts']): number {
  return counts.failedRetryable + counts.failedPermanent + (counts.notAttempted ?? 0);
}

/** What a finished run left behind: its failures plus the products it never reached. */
function runLeftOver(run: BatchRun): number {
  return (run.failed ?? 0) + (run.notAttempted ?? 0);
}

function fixSub(key: StepKey, toFix: number): StepperItem {
  return toFix > 0
    ? { key, sub: `${formatCount(toFix)} to fix`, tone: 'bad' }
    : { key, sub: 'All good', tone: 'ok' };
}

/**
 * A post-step's sub-label: what a retry fixes, plus what needs a person (gave up, no keywords) and
 * what waits on search descriptors — never "All good" while any of those remain.
 */
function postStepSub(key: StepKey, a: StepAttention): StepperItem {
  const parts = [
    a.toFix > 0 ? `${formatCount(a.toFix)} to fix` : null,
    a.gaveUp > 0 ? `${formatCount(a.gaveUp)} gave up` : null,
    a.needsKeywords > 0 ? `${formatCount(a.needsKeywords)} need keywords` : null,
    a.waiting > 0 ? `${formatCount(a.waiting)} waiting` : null,
  ].filter((p): p is string => p !== null);
  if (parts.length === 0) return { key, sub: 'All good', tone: 'ok' };
  const needsAction = a.toFix + a.gaveUp + a.needsKeywords > 0;
  return { key, sub: parts.join(' · '), tone: needsAction ? 'bad' : 'neutral' };
}

/** The batch as a timeline: the original run plus each retry, with outcomes per run. */

/** Static class names (Tailwind scans source): totals columns by tile count, one row at lg+. */
const TOTALS_COLS: Record<number, string> = {
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6',
};

export function BatchRunsTab({
  jobId,
  onRetryRun,
  onRetryRemaining,
  onExclude,
  onRetryPostStep,
  focusProducts = false,
  onProductsFocused,
  focusCarryover = false,
  onCarryoverFocused,
}: BatchRunsTabProps) {
  const { data, isLoading, isError } = useBatchRuns(jobId);
  // Null (older backend, 404) or an error: nothing new renders and the rest is unchanged.
  const { data: carryover } = useBatchCarryover(jobId);
  // Product journey: absent `postSteps` (older backend) or a 404 on the journey read = today's
  // Enrichment UI with no stepper.
  const { data: postSteps } = usePostSteps(jobId);
  const delivered =
    data?.batchStatus === 'COMPLETED' || data?.batchStatus === 'COMPLETED_WITH_ISSUES';
  // Probe an enabled step only (never a disabled SD / PD). With both off, a delivered batch probes
  // Delivery — the same read (and cache entry) its Delivery view makes; an undelivered one reads
  // nothing, since its Delivery view shows a message and Enrichment needs no journey data.
  const probeStep: JourneyStep | null = postSteps?.searchDescriptors
    ? 'SEARCH_DESCRIPTORS'
    : postSteps?.phraseDescriptions
      ? 'PHRASE_DESCRIPTIONS'
      : delivered
        ? 'DELIVERY'
        : null;
  // Only probe once the batch status is in: before it, "delivered" reads false and the probe
  // could pick the wrong step.
  const journeyProbe = useJourneyRuns(
    jobId,
    probeStep ?? 'SEARCH_DESCRIPTORS',
    data !== undefined && probeStep !== null,
  );
  // Decide once, so the stepper never shows and then disappears: the batch status must be in, and
  // a probe must have answered (null = its 404 → today's table; an error keeps the stepper).
  const probeSettled = journeyProbe.data !== undefined || journeyProbe.isError;
  const journeyOn =
    postSteps != null &&
    data !== undefined &&
    (probeStep === null || (probeSettled && journeyProbe.data !== null));
  const { data: header } = useBatchHeader(jobId, { enabled: journeyOn });
  const [pickedStep, setPickedStep] = useState<StepKey>('ENRICHMENT');
  const [gridFocus, setGridFocus] = useState<GridFocus | null>(null);
  const carryoverRef = useRef<HTMLDivElement>(null);
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const runParam = search.run ?? null;
  // `?cls=FAILED|NOT_ATTEMPTED` opens the selected run's products on that filter.
  const classParam: RunFilter =
    search.cls === 'FAILED' || search.cls === 'NOT_ATTEMPTED' ? search.cls : 'ALL';
  // `?na=1` (the grid's Needs Attention link) lands on the original run's products.
  const landOnRoot = search.na === 1;

  // Pushes history (like the chips) so Back returns to the previous selection.
  function selectRun(run: string | undefined, cls?: 'FAILED' | 'NOT_ATTEMPTED') {
    void navigate({
      to: '.',
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        run,
        cls,
        na: undefined,
      })) as never,
    });
  }

  const productsRef = useRef<HTMLDivElement>(null);
  const hasData = !!data;
  function scrollToProducts() {
    productsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  // The grid sits below the totals; a link from the top of the page would otherwise switch the
  // tab with nothing visible changing.
  useEffect(() => {
    if (!focusProducts || !hasData) return;
    scrollToProducts();
    onProductsFocused?.();
  }, [focusProducts, hasData, onProductsFocused]);

  useEffect(() => {
    if (!focusCarryover || !hasData) return;
    carryoverRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    onCarryoverFocused?.();
  }, [focusCarryover, hasData, onCarryoverFocused]);

  if (isLoading) return <Skeleton data-testid="batch-runs-loading" className="h-64 w-full" />;
  if (isError || !data) {
    return (
      <p data-testid="batch-runs-error" className="text-error text-sm">
        Could not load the runs for this batch.
      </p>
    );
  }

  const { counts } = data;
  const left = leftOver(counts);
  const recovered = data.runs
    .filter((r) => r.kind === 'RETRY')
    .reduce((sum, r) => sum + (r.succeeded ?? 0), 0);
  const nameById = new Map(data.runs.map((r) => [r.runId, runName(r)]));
  const canExclude = data.availableActions.includes('EXCLUDE_REMAINING');
  const canRetryRemaining = data.availableActions.includes('RETRY_REMAINING');
  // A retry takes retryable failures plus never-reached products; permanent failures stay.
  const retryable = counts.failedRetryable + (counts.notAttempted ?? 0);
  const latest = data.runs[data.runs.length - 1] ?? null;
  // `?run=` selects a run; default to the latest one (the root when there are no retries).
  const selected =
    data.runs.find((r) => r.runId === runParam) ??
    (landOnRoot ? data.runs.find((r) => r.kind === 'ROOT') : undefined) ??
    latest;

  const carriedInTotal = carryover?.carriedIn?.total ?? 0;
  const movedOutTotal = carryover?.movedOut?.total ?? 0;
  const root = data.runs.find((r) => r.kind === 'ROOT');

  /** "View ›" in the carry-over table: the original run's grid, narrowed to that batch. */
  function viewCarryover(view: CarryoverView) {
    selectRun(root?.runId, undefined);
    setGridFocus({
      key: Date.now(),
      batchId: view.batchId,
      ...(view.side === 'MOVED' ? { outcome: 'MOVED' as const } : { source: 'EARLIER' as const }),
    });
    scrollToProducts();
  }

  // Null until the header loads, so a GMC batch never flashes the non-GMC Delivery copy.
  const isGmc = header ? /GMC|GOOGLE/i.test(header.connectorCode ?? '') : null;
  const step: StepKey =
    journeyOn && isStepEnabled(postSteps, pickedStep) ? pickedStep : 'ENRICHMENT';
  const stepperItems: StepperItem[] = JOURNEY_STEPS.map((key) => {
    if (!isStepEnabled(postSteps, key)) {
      return { key, sub: 'Not enabled for this batch', tone: 'neutral', disabled: true };
    }
    if (key === 'ENRICHMENT') return fixSub(key, left);
    if (key === 'DELIVERY') {
      if (!delivered) return { key, sub: 'Awaiting approval', tone: 'neutral' };
      // Redeliver exists for Google Merchant only; any finished retry with unsent fixes counts.
      if (isGmc !== true) return { key, sub: 'Delivered', tone: 'neutral' };
      return (postSteps?.pendingRedeliveries ?? 0) > 0
        ? { key, sub: 'Ready to redeliver', tone: 'bad' }
        : { key, sub: 'All good', tone: 'ok' };
    }
    return postStepSub(key, attentionOf(key, healthOf(postSteps, key)));
  });

  return (
    <section data-testid="batch-runs-tab" className="space-y-4 rounded-lg border bg-white p-5">
      <header className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold">Runs</h2>
        <div className="flex flex-wrap items-center gap-2">
          {step === 'ENRICHMENT' && (
            <span
              data-testid="batch-runs-attempts-pill"
              className="bg-muted rounded-full px-2.5 py-0.5 text-xs font-medium"
            >
              Retries used {data.attemptsUsed} of {data.maxAttempts}
            </span>
          )}
          {step === 'ENRICHMENT' && canRetryRemaining && (
            <Button
              size="sm"
              data-testid="batch-runs-retry-remaining"
              className="cursor-pointer"
              onClick={onRetryRemaining}
            >
              <RotateCw aria-hidden="true" /> Retry remaining ({formatCount(retryable)})
            </Button>
          )}
        </div>
      </header>

      {journeyOn && <PostStepStepper steps={stepperItems} active={step} onSelect={setPickedStep} />}

      {journeyOn && step !== 'ENRICHMENT' && (
        <PostStepJourney
          jobId={jobId}
          step={step}
          postSteps={postSteps}
          enrichedCount={counts.enriched}
          delivered={delivered}
          isGmc={isGmc}
          onRetry={(retryStep, scope) => {
            onRetryPostStep?.(retryStep, scope);
          }}
        />
      )}

      {step === 'ENRICHMENT' && (
        <>
          <dl
            data-testid="batch-runs-totals"
            // One row on wide screens however many tiles carry-over adds (4, 5 or 6).
            className={cn(
              'grid grid-cols-2 gap-2',
              TOTALS_COLS[4 + (carriedInTotal > 0 ? 1 : 0) + (movedOutTotal > 0 ? 1 : 0)],
            )}
          >
            {[
              ['Batch curated', formatCount(counts.curated), 'curated'],
              ['Enriched so far', formatCount(counts.enriched), 'enriched'],
              [
                'Still not enriched',
                counts.excluded > 0
                  ? `${formatCount(left)} (+${formatCount(counts.excluded)} excluded)`
                  : formatCount(left),
                'left',
              ],
              ['Recovered by retries', formatCount(recovered), 'recovered'],
              ...(carriedInTotal > 0
                ? [['From earlier batches', formatCount(carriedInTotal), 'carried-in']]
                : []),
              ...(movedOutTotal > 0
                ? [['Moved to later batches', formatCount(movedOutTotal), 'moved-out']]
                : []),
            ].map(([label, value, key]) => {
              const showLeft = key === 'left' && left > 0 && latest !== null;
              return (
                <div
                  key={key}
                  className={cn(
                    'relative rounded-lg border px-3 py-2',
                    showLeft && 'border-warning/40 bg-warning-50 hover:bg-warning-50/70',
                  )}
                >
                  <dt className="text-muted-foreground text-xs">
                    {showLeft ? (
                      // Stretched over the tile: the products still not enriched are the latest run's
                      // leftovers, so the tile opens that run's grid on them (no separate drawer).
                      <button
                        type="button"
                        data-testid="batch-runs-show-left"
                        className="cursor-pointer after:absolute after:inset-0"
                        onClick={() => {
                          selectRun(latest.runId, leftOverFilter(latest));
                          scrollToProducts();
                        }}
                      >
                        {label}
                      </button>
                    ) : (
                      label
                    )}
                  </dt>
                  <dd
                    data-testid={`batch-runs-total-${String(key)}`}
                    className="text-lg font-semibold tabular-nums"
                  >
                    {value}
                  </dd>
                </div>
              );
            })}
          </dl>

          {carryover && (
            <div ref={carryoverRef} className="scroll-mt-4">
              <BatchCarryoverSection carryover={carryover} onView={viewCarryover} />
            </div>
          )}

          {isRetryStuck(data) && (
            <div
              data-testid="batch-runs-stuck-panel"
              className="bg-warning-50 border-warning/40 flex flex-wrap items-start justify-between gap-3 rounded-lg border p-4"
            >
              <div className="min-w-0 flex-1 basis-72 space-y-1">
                <p className="text-sm font-semibold">
                  {formatCount(left)} product{left === 1 ? '' : 's'} could not be enriched
                  {data.attemptsUsed >= data.maxAttempts
                    ? ` after ${String(data.maxAttempts)} retries`
                    : ' and cannot be retried'}
                </p>
                <p className="text-muted-foreground text-xs">
                  Retries are used up. You can approve the batch without them, or exclude them from
                  this batch so they stop showing as issues. Excluded products stay in your catalog
                  unenriched; the next run picks them up again.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="batch-runs-stuck-see-why"
                  className="cursor-pointer"
                  onClick={() => {
                    const root = data.runs.find((r) => r.kind === 'ROOT');
                    if (root) selectRun(root.runId, 'FAILED');
                  }}
                >
                  <Eye /> See why
                </Button>
                {canExclude && (
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="batch-runs-stuck-exclude"
                    className="cursor-pointer"
                    onClick={onExclude}
                  >
                    <Ban /> Exclude from this batch
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Runs : products = 1 : 3 */}
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,3fr)]">
            <ol data-testid="batch-runs-timeline" aria-label="Runs" className="flex flex-col">
              {data.runs.map((run, index) => (
                <RunRow
                  key={run.runId}
                  run={run}
                  isLast={index === data.runs.length - 1}
                  pickedUpBy={data.runs[index + 1]}
                  selected={selected?.runId === run.runId}
                  parentName={run.parentRunId ? nameById.get(run.parentRunId) : undefined}
                  onSelect={() => {
                    selectRun(run.runId);
                  }}
                  onRetry={() => {
                    onRetryRun(run.runId);
                  }}
                />
              ))}
            </ol>
            {selected && (
              <div ref={productsRef} className="min-w-0 scroll-mt-4">
                <RunProductsPanel
                  jobId={jobId}
                  run={selected}
                  initialFilter={classParam}
                  carryover={carryover ?? null}
                  focus={gridFocus}
                />
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

interface RunRowProps {
  run: BatchRun;
  isLast: boolean;
  /** The next run, which picked up what this one left; absent on the latest run. */
  pickedUpBy: BatchRun | undefined;
  selected: boolean;
  parentName: string | undefined;
  onSelect: () => void;
  onRetry: () => void;
}

function RunRow({ run, isLast, pickedUpBy, selected, parentName, onSelect, onRetry }: RunRowProps) {
  const status = run.status ?? 'RUNNING';
  const picked = runTotal(run);
  const finished = run.picked !== null && (run.succeeded !== null || run.failed !== null);
  const meta = [
    run.scope ? SCOPE_LABEL[run.scope] : null,
    parentName ? `from ${parentName}` : null,
    run.startedAt ? formatDateTime(run.startedAt) : null,
  ].filter(Boolean);
  const left = runLeftOver(run);
  // Only the latest run offers "Retry what it left": a later run already picked up the rest.
  const canRetry = isLast && run.availableActions.includes('RETRY_RUN_FAILURES') && left > 0;
  const barLabel = `${formatCount(run.succeeded)} enriched, ${formatCount(run.failed)} failed, ${formatCount(run.notAttempted)} not reached of ${formatCount(run.picked)} picked`;

  return (
    <li
      data-testid={`batch-run-row-${run.runId}`}
      data-selected={selected}
      aria-current={selected ? 'true' : undefined}
      className="grid grid-cols-[20px_minmax(0,1fr)] gap-3"
    >
      <div className="relative flex justify-center">
        <span
          className={cn(
            'z-10 mt-1 size-3 rounded-full ring-4 ring-white',
            NODE_TONE[status] ?? 'bg-muted-foreground',
          )}
        />
        {!isLast && <span className="bg-border absolute top-5 -bottom-1 w-0.5" />}
      </div>
      <div
        className={cn(
          'relative mb-3 flex min-w-0 flex-col gap-1.5 rounded-lg border p-3 transition-colors',
          selected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50 border-transparent',
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              title={run.runId}
              data-testid={`batch-run-select-${run.runId}`}
              aria-pressed={selected}
              onClick={onSelect}
              // Stretched over the card: clicking anywhere on it shows this run's products.
              className="cursor-pointer text-sm font-semibold after:absolute after:inset-0 after:rounded-lg"
            >
              {runName(run)}
            </button>
            <span
              data-testid={`batch-run-status-${run.runId}`}
              className={cn(
                'rounded-full px-2 py-0.5 text-xs font-medium',
                STATUS_TONE[status] ?? 'bg-muted text-muted-foreground',
              )}
            >
              {humanize(status)}
            </span>
          </div>
          {canRetry && (
            <Button
              variant="outline"
              size="sm"
              data-testid={`batch-run-retry-${run.runId}`}
              // Above the card's stretched select target.
              className="relative z-10 shrink-0 cursor-pointer"
              onClick={onRetry}
            >
              Retry {formatCount(left)} <ChevronRight />
            </Button>
          )}
        </div>
        {meta.length > 0 && <p className="text-muted-foreground text-xs">{meta.join(' · ')}</p>}
        {run.failureReason && <p className="text-error text-xs">{run.failureReason}</p>}

        {finished ? (
          <RunOutcomeBar
            run={run}
            total={picked}
            testId={`batch-run-bar-${run.runId}`}
            label={barLabel}
          />
        ) : (
          <div
            role="img"
            aria-label={barLabel}
            data-testid={`batch-run-bar-${run.runId}`}
            className="bg-muted flex h-2 overflow-hidden rounded-full"
          >
            <span className="bg-info h-2 w-2/5 opacity-50" />
          </div>
        )}
        <div className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs">
          <span>
            Picked <b className="text-foreground">{formatCount(run.picked)}</b>
            {run.status === 'RUNNING' && run.planned !== null && run.planned !== run.picked
              ? ` of ${formatCount(run.planned)}`
              : ''}
          </span>
          {run.done !== null && (
            <span data-testid={`batch-run-done-${run.runId}`}>
              Done so far <b className="text-foreground">{formatCount(run.done)}</b>
            </span>
          )}
          <RunOutcomeLegend run={run} />
        </div>
        {pickedUpBy && left > 0 && (
          <p
            data-testid={`batch-run-picked-up-${run.runId}`}
            className="text-muted-foreground text-xs"
          >
            {formatCount(left)} left → picked up by {runName(pickedUpBy)}
          </p>
        )}
      </div>
    </li>
  );
}
